import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import CommandLayout from "@/components/CommandLayout";
import {
  Loader2, ArrowLeft, Plus, Copy, Zap, Star, Film, MessageSquare,
  ChevronDown, ChevronUp, Flame, Eye, Send, Youtube, Hash, CheckCircle2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";

/* ─── Types ─── */

interface PlatformHooks {
  tiktok?: string;
  reels?: string;
  youtube_shorts?: string;
}

interface SocialRequest {
  id: string;
  platform: string;
  username: string;
  comment_text: string;
  status: string;
  is_viral: boolean;
  toxicity_score: number | null;
  manipulation_type: string | null;
  neutralized_response: string | null;
  kling_prompt: string | null;
  overlay_text: string | null;
  viral_hooks: string[];
  caption: string | null;
  youtube_title: string | null;
  youtube_description: string | null;
  youtube_pinned_comment: string | null;
  youtube_thumbnail_prompt: string | null;
  youtube_tags: string[];
  platform_hooks: PlatformHooks | null;
  created_at: string;
}

/** Unified queue item — can come from neutralizations OR social_requests */
interface QueueItem {
  id: string;
  source: "neutralization" | "social_request";
  original_text: string;
  toxicity_score: number;
  manipulation_type: string | null;
  neutralized_response: string | null;
  kling_prompt: string;
  youtube_title: string;
  youtube_description: string;
  youtube_pinned_comment: string;
  youtube_thumbnail_prompt: string;
  youtube_tags: string[];
  platform_hooks: PlatformHooks;
  overlay_text: string;
  viral_hooks: string[];
  caption: string;
  status: string;
  is_viral: boolean;
  created_at: string;
  // social_request fields for status sync
  sr_id?: string;
}

/* ─── Helpers ─── */

const MANIPULATION_LABELS: Record<string, string> = {
  guilt_induction: "Guilt Induction",
  shame_attack: "Shame Attack",
  gaslighting: "Gaslighting",
  urgency_pressure: "Urgency Pressure",
  authority_dominance: "Authority Dominance",
  victim_reversal: "Victim Reversal",
  passive_aggression: "Passive Aggression",
  blame_shift: "Blame Shift",
  emotional_blackmail: "Emotional Blackmail",
  social_pressure: "Social Pressure",
};

const generateKlingPrompt = (text: string, tactic: string, score: number) => {
  const label = MANIPULATION_LABELS[tactic] || tactic?.replace(/_/g, " ") || "Manipulation";
  return [
    `SCENE 1 (0-5s): Close up, subject looking stressed by phone glow, holographic text overlay showing "${text.slice(0, 120)}${text.length > 120 ? "…" : ""}". 4k, psychological thriller lighting, dark room, anamorphic lens.`,
    `SCENE 2 (5-10s): Cut to wider shot, red light pulses as "${label}" label appears. The score ${score.toFixed(1)}/10 flashes on screen. Cinematic color grading, dramatic tension.`,
    `SCENE 3 (10-15s): Subject takes a deep breath, expression turns neutral/calm (GreyRock state). Screen fades to black with text: greyrockai.app. Peaceful ambient lighting.`,
  ].join("\n\n");
};

const generateYouTubeTitle = (tactic: string, score: number) => {
  const label = MANIPULATION_LABELS[tactic] || tactic?.replace(/_/g, " ") || "Manipulation";
  const title = `${label} Analysis 2026: Why this text is a ${score.toFixed(1)}/10`;
  return title.slice(0, 60);
};

const generateYouTubeDesc = (tactic: string) => {
  const label = MANIPULATION_LABELS[tactic] || "toxic manipulation";
  return `Exposing ${label.toLowerCase()} tactics with the GreyRock AI engine. Learn how to identify and neutralize ${label.toLowerCase()} in your daily life.\n🛡️ Analyze your own texts at greyrockai.app`;
};

const generatePlatformHooks = (tactic: string, score: number): PlatformHooks => {
  const label = MANIPULATION_LABELS[tactic] || tactic?.replace(/_/g, " ") || "manipulation";
  return {
    tiktok: `POV: You just caught a ${label.toLowerCase()} in the wild 🎯`,
    reels: `POV: Someone sent THIS after you set a boundary 🚩 ${score.toFixed(1)}/10`,
    youtube_shorts: `This text scored a ${score.toFixed(1)}/10 on the toxicity scale... here's why 🧠`,
  };
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  analyzed: "bg-cyan/20 text-cyan border-cyan/30",
  backlog: "bg-cyan/20 text-cyan border-cyan/30",
  prompted: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  posted: "bg-green-500/20 text-green-400 border-green-500/30",
};

const PLATFORM_ICONS: Record<string, string> = {
  tiktok: "🎵", instagram: "📸", youtube: "▶️", twitter: "𝕏", facebook: "📘",
};

/* ─── Component ─── */

const SocialWarRoom = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  // Data
  const [socialRequests, setSocialRequests] = useState<SocialRequest[]>([]);
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(false);

  // UI
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);

  // New request form
  const [newPlatform, setNewPlatform] = useState("tiktok");
  const [newUsername, setNewUsername] = useState("");
  const [newComment, setNewComment] = useState("");

  useEffect(() => {
    const checkAdmin = async () => {
      if (authLoading) return;
      if (!user) { navigate("/dashboard", { replace: true }); return; }
      const { data: isAdmin } = await supabase.rpc("is_admin_user");
      if (!isAdmin) { navigate("/dashboard", { replace: true }); return; }
      setChecking(false);
      fetchAllData();
    };
    checkAdmin();
  }, [user, authLoading]);

  const fetchAllData = async () => {
    setLoading(true);
    // Fetch both data sources in parallel
    const [srResult, neutResult] = await Promise.all([
      supabase.from("social_requests").select("*").order("created_at", { ascending: false }),
      supabase.from("neutralizations").select("*").order("toxicity_score", { ascending: false }).limit(100),
    ]);

    const srData = (srResult.data || []) as any as SocialRequest[];
    setSocialRequests(srData);

    // Build unified queue from neutralizations
    const neutItems: QueueItem[] = (neutResult.data || []).map((n: any) => {
      const score = Number(n.toxicity_score) || 0;
      const tactic = n.manipulation_primary_type || "passive_aggression";
      return {
        id: `neut-${n.id}`,
        source: "neutralization" as const,
        original_text: n.raw_text,
        toxicity_score: score,
        manipulation_type: tactic,
        neutralized_response: n.grey_rock_response,
        kling_prompt: generateKlingPrompt(n.raw_text, tactic, score),
        youtube_title: generateYouTubeTitle(tactic, score),
        youtube_description: generateYouTubeDesc(tactic),
        youtube_pinned_comment: "Protect your peace. Analyze your own texts at greyrockai.app 🛡️",
        youtube_thumbnail_prompt: `Close-up emotional reaction with large, high-contrast text overlay: ${score.toFixed(1)}/10, dramatic lighting, bold red/blue accents`,
        youtube_tags: ["grey rock method", "manipulation tactics 2026", tactic.replace(/_/g, " "), "toxic relationship", "narcissist", "emotional intelligence", "greyrock ai", "mental health"],
        platform_hooks: generatePlatformHooks(tactic, score),
        overlay_text: `Toxicity Score: ${score.toFixed(1)}/10 | Tactic: ${MANIPULATION_LABELS[tactic] || tactic} | GreyRock Response Active`,
        viral_hooks: [
          `This ${(MANIPULATION_LABELS[tactic] || tactic).toLowerCase()} scored ${score.toFixed(1)}/10 💀`,
          `The AI decoded what they were REALLY saying...`,
          `Grey Rock response activated. Watch this 🪨`,
        ],
        caption: `🚨 ${MANIPULATION_LABELS[tactic] || tactic} detected — ${score.toFixed(1)}/10 toxicity. The AI broke it down. #GreyRockAI #SilenceIsPower #ToxicPeopleExposed`,
        status: n.content_status || "backlog",
        is_viral: false,
        created_at: n.created_at,
      };
    });

    // Also include analyzed social_requests in queue
    const srItems: QueueItem[] = srData
      .filter(r => r.status !== "pending")
      .map(r => ({
        id: `sr-${r.id}`,
        source: "social_request" as const,
        original_text: r.comment_text,
        toxicity_score: Number(r.toxicity_score) || 0,
        manipulation_type: r.manipulation_type,
        neutralized_response: r.neutralized_response,
        kling_prompt: r.kling_prompt || "",
        youtube_title: r.youtube_title || "",
        youtube_description: r.youtube_description || "",
        youtube_pinned_comment: r.youtube_pinned_comment || "",
        youtube_thumbnail_prompt: r.youtube_thumbnail_prompt || "",
        youtube_tags: r.youtube_tags || [],
        platform_hooks: (r.platform_hooks as PlatformHooks) || {},
        overlay_text: r.overlay_text || "",
        viral_hooks: r.viral_hooks || [],
        caption: r.caption || "",
        status: r.status,
        is_viral: r.is_viral,
        created_at: r.created_at,
        sr_id: r.id,
      }));

    // Merge and sort by toxicity descending
    const merged = [...neutItems, ...srItems].sort((a, b) => b.toxicity_score - a.toxicity_score);
    setQueueItems(merged);
    setLoading(false);
  };

  /* ─── Social Request actions ─── */

  const handleAddRequest = async () => {
    if (!newComment.trim() || !newUsername.trim()) {
      toast({ title: "Missing fields", description: "Username and comment are required.", variant: "destructive" });
      return;
    }
    const { error } = await supabase.from("social_requests").insert({
      user_id: user!.id,
      platform: newPlatform,
      username: newUsername.trim(),
      comment_text: newComment.trim(),
    });
    if (error) {
      toast({ title: "Error", description: "Failed to add request.", variant: "destructive" });
      return;
    }
    toast({ title: "✅ Request Added", description: "Social request queued for analysis." });
    setShowAddModal(false);
    setNewUsername("");
    setNewComment("");
    fetchAllData();
  };

  const handleAnalyze = async (id: string) => {
    setAnalyzingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("analyze-social", {
        body: { action: "analyze", request_id: id },
      });
      if (error || data?.error) throw new Error(data?.error || "Analysis failed");
      toast({ title: "🔥 Analysis Complete", description: "Content package generated." });
      fetchAllData();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleStatusChange = async (item: QueueItem, status: string) => {
    if (item.source === "social_request" && item.sr_id) {
      await supabase.functions.invoke("analyze-social", {
        body: { action: "update_status", request_id: item.sr_id, status },
      });
    }
    setQueueItems(prev => prev.map(q => q.id === item.id ? { ...q, status } : q));
  };

  const handleViralToggle = async (item: QueueItem, is_viral: boolean) => {
    if (item.source === "social_request" && item.sr_id) {
      await supabase.functions.invoke("analyze-social", {
        body: { action: "toggle_viral", request_id: item.sr_id, is_viral },
      });
    }
    setQueueItems(prev => prev.map(q => q.id === item.id ? { ...q, is_viral } : q));
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: `${label} copied to clipboard.` });
  };

  const handleCopyPromptAndSync = (item: QueueItem) => {
    if (item.kling_prompt) {
      navigator.clipboard.writeText(item.kling_prompt);
      handleStatusChange(item, "prompted");
      toast({ title: "📋 Prompt Copied", description: "Status updated to PROMPTED." });
    }
  };

  const handleMarkAsPosted = (item: QueueItem) => {
    handleStatusChange(item, "posted");
    toast({ title: "✅ Marked as Posted", description: "Status updated and counter incremented." });
  };

  const copyPlatformPackage = (item: QueueItem, platform: "tiktok" | "reels" | "youtube_shorts") => {
    const hook = item.platform_hooks?.[platform] || "";
    const parts = [
      `HOOK: ${hook}`,
      `\nCAPTION:\n${item.caption || ""}`,
      `\nOVERLAY: ${item.overlay_text || ""}`,
    ];
    if (platform === "youtube_shorts") {
      parts.push(`\nTITLE: ${item.youtube_title || ""}`);
      parts.push(`\nDESCRIPTION:\n${item.youtube_description || ""}`);
      parts.push(`\nTAGS: ${(item.youtube_tags || []).join(", ")}`);
      parts.push(`\nPINNED COMMENT:\n${item.youtube_pinned_comment || ""}`);
    }
    copyToClipboard(parts.join("\n"), `${platform} package`);
  };

  const getToxColor = (score: number) => {
    if (score >= 8) return "text-red-500";
    if (score >= 5) return "text-yellow-400";
    return "text-cyan";
  };

  const getToxBorder = (score: number) => {
    if (score >= 8) return "border-l-2 border-l-red-500 bg-red-500/5";
    return "";
  };

  const getStatusAccent = (status: string) => {
    if (status === "posted" || status === "prompted") return "border-l-2 border-l-cyan bg-cyan/5";
    return "";
  };

  const pendingSocialRequests = socialRequests.filter(r => r.status === "pending");
  const postedCount = queueItems.filter(q => q.status === "posted").length;
  const viralCount = queueItems.filter(q => q.is_viral).length;

  if (authLoading || checking) {
    return (
      <CommandLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-cyan" />
        </div>
      </CommandLayout>
    );
  }

  return (
    <CommandLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-foreground">Social </span>
              <span className="text-gradient-cyan">Command Center</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Content Engine · Analyze · Generate · Post</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddModal(true)} className="bg-cyan hover:bg-cyan/80 text-charcoal-deep font-semibold">
              <Plus className="w-4 h-4 mr-1" /> New Request
            </Button>
            <Button variant="outline" size="sm" asChild className="border-cyan/30 text-cyan hover:bg-cyan/10">
              <Link to="/admin-portal"><ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard</Link>
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Pending", value: pendingSocialRequests.length, icon: MessageSquare, color: "text-yellow-400" },
            { label: "In Queue", value: queueItems.length, icon: Film, color: "text-cyan" },
            { label: "Viral", value: viralCount, icon: Flame, color: "text-red-500" },
            { label: "Posted", value: postedCount, icon: Send, color: "text-green-400" },
          ].map(s => (
            <Card key={s.label} className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <div>
                  <p className="text-2xl font-bold font-mono text-foreground">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* SECTION 1: Incoming Social Requests Inbox */}
        <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-yellow-400" />
              Incoming Requests
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 ml-2">{pendingSocialRequests.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-glass-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Platform</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Username</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Comment</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground w-[140px]">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingSocialRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No pending requests. Click "New Request" to add one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingSocialRequests.map(r => (
                      <TableRow key={r.id} className="border-glass-border">
                        <TableCell className="text-lg">{PLATFORM_ICONS[r.platform] || "📱"} <span className="text-xs text-muted-foreground capitalize">{r.platform}</span></TableCell>
                        <TableCell className="text-sm font-mono text-cyan">@{r.username}</TableCell>
                        <TableCell className="max-w-[300px]">
                          <p className="text-xs text-muted-foreground truncate">{r.comment_text}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[r.status] || ""}>{r.status.toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => handleAnalyze(r.id)}
                            disabled={analyzingId === r.id}
                            className="bg-cyan/20 text-cyan hover:bg-cyan/30 border border-cyan/30 text-xs"
                          >
                            {analyzingId === r.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                            Analyze
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* SECTION 2: Unified Kling Content Queue — sorted by toxicity desc */}
        <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Film className="w-5 h-5 text-cyan" />
              Ready for Kling · Content Queue
              <Badge className="bg-cyan/20 text-cyan border-cyan/30 ml-2">{queueItems.length}</Badge>
              <span className="text-[10px] text-muted-foreground ml-auto font-normal">Sorted by Toxicity ↓ · Sources: Neutralizations + Social</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="max-h-[600px]">
              {queueItems.length === 0 ? (
                <div className="text-center text-muted-foreground py-12">
                  No content yet. Analyze messages in the Neutralizer Lab to populate this queue.
                </div>
              ) : (
                <div className="divide-y divide-glass-border">
                  {queueItems.map(item => {
                    const isHighTox = item.toxicity_score >= 8;
                    const isNeutralized = item.status === "posted" || item.status === "prompted";
                    const rowAccent = isHighTox ? getToxBorder(item.toxicity_score) : isNeutralized ? getStatusAccent(item.status) : "";

                    return (
                      <div key={item.id} className={`p-4 ${rowAccent} ${item.is_viral ? "ring-1 ring-inset ring-red-500/20" : ""}`}>
                        {/* Row header */}
                        <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                          <div className="flex items-center gap-3 min-w-0 flex-wrap">
                            <Badge className={`text-[9px] ${item.source === "neutralization" ? "bg-cyan/10 text-cyan border-cyan/20" : "bg-purple-500/10 text-purple-400 border-purple-500/20"}`}>
                              {item.source === "neutralization" ? "🧠 NEUT" : "📱 SOCIAL"}
                            </Badge>
                            <span className={`font-mono font-bold text-sm ${getToxColor(item.toxicity_score)}`}>
                              {item.toxicity_score.toFixed(1)}/10
                            </span>
                            {isHighTox && <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider animate-pulse">🔴 HOT</span>}
                            <Badge className="text-[10px] capitalize bg-charcoal-dark border-glass-border">
                              {item.manipulation_type?.replace(/_/g, " ") || "—"}
                            </Badge>
                            {item.is_viral && <Flame className="w-4 h-4 text-red-500" />}
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{item.original_text.slice(0, 60)}…</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={STATUS_COLORS[item.status] || "bg-charcoal-dark border-glass-border text-muted-foreground"}>
                              {item.status.toUpperCase()}
                            </Badge>
                            {expandedId === item.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                          </div>
                        </div>

                        {/* Expanded content */}
                        {expandedId === item.id && (
                          <div className="mt-4 space-y-4 pl-2 border-l-2 border-cyan/20 ml-3">
                            {/* Original Text */}
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Original Text</p>
                              <p className={`text-sm bg-charcoal-deep/60 rounded p-3 font-mono ${isHighTox ? "text-red-300 border border-red-500/20" : "text-foreground/80"}`}>{item.original_text}</p>
                            </div>

                            {/* Neutralized Response */}
                            {item.neutralized_response && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-cyan mb-1">🛡️ Grey Rock Response</p>
                                <div className="flex items-center justify-between bg-cyan/5 rounded p-3 border border-cyan/20">
                                  <p className="text-sm text-cyan font-mono">{item.neutralized_response}</p>
                                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.neutralized_response!, "Response")} className="h-6 text-xs text-cyan ml-2 shrink-0">
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* 2026 Distribution Hub — Multi-Platform Tabs */}
                            <div className="bg-charcoal-deep/40 rounded-lg p-4 border border-glass-border space-y-3">
                              <p className="text-xs uppercase tracking-wider text-cyan font-semibold">🌐 2026 Distribution Hub</p>
                              <Tabs defaultValue="tiktok" className="w-full">
                                <TabsList className="bg-charcoal-deep border border-glass-border w-full grid grid-cols-3">
                                  <TabsTrigger value="tiktok" className="text-xs data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">🎵 TikTok</TabsTrigger>
                                  <TabsTrigger value="reels" className="text-xs data-[state=active]:bg-cyan/20 data-[state=active]:text-cyan">📸 Reels</TabsTrigger>
                                  <TabsTrigger value="youtube_shorts" className="text-xs data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">▶️ YT Shorts</TabsTrigger>
                                </TabsList>
                                {(["tiktok", "reels", "youtube_shorts"] as const).map(platform => (
                                  <TabsContent key={platform} value={platform} className="mt-2 space-y-2">
                                    <div className="flex items-center justify-between bg-charcoal-deep/80 rounded p-3 border border-glass-border">
                                      <p className="text-xs text-foreground/80 font-mono flex-1">
                                        {item.platform_hooks?.[platform] || "No hook generated"}
                                      </p>
                                      {item.platform_hooks?.[platform] && (
                                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.platform_hooks[platform]!, `${platform} hook`)} className="h-6 text-xs text-cyan ml-2 shrink-0">
                                          <Copy className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => copyPlatformPackage(item, platform)}
                                      className={`w-full text-xs ${platform === "youtube_shorts" ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30" : "bg-cyan/10 text-cyan hover:bg-cyan/20 border border-cyan/20"}`}
                                    >
                                      <Copy className="w-3 h-3 mr-1" /> Copy All {platform === "youtube_shorts" ? "YouTube" : platform === "reels" ? "Reels" : "TikTok"} Package
                                    </Button>
                                  </TabsContent>
                                ))}
                              </Tabs>
                            </div>

                            {/* 3-Scene Kling Prompt */}
                            {item.kling_prompt && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-[10px] uppercase tracking-wider text-cyan">🎬 Kling 3.0 Multi-Shot Prompt</p>
                                  <Button
                                    size="sm"
                                    onClick={() => handleCopyPromptAndSync(item)}
                                    className="bg-cyan/20 text-cyan hover:bg-cyan/30 border border-cyan/30 text-xs h-7"
                                  >
                                    <Copy className="w-3 h-3 mr-1" />Copy Prompt → PROMPTED
                                  </Button>
                                </div>
                                <div className="text-xs text-foreground/70 bg-charcoal-deep/80 rounded p-3 font-mono leading-relaxed border border-cyan/10 whitespace-pre-wrap">{item.kling_prompt}</div>
                              </div>
                            )}

                            {/* YouTube Thumbnail Prompt */}
                            {item.youtube_thumbnail_prompt && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-[10px] uppercase tracking-wider text-red-400">🖼️ YouTube Thumbnail Prompt</p>
                                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.youtube_thumbnail_prompt, "Thumbnail prompt")} className="h-6 text-xs text-red-400">
                                    <Copy className="w-3 h-3 mr-1" />Copy
                                  </Button>
                                </div>
                                <p className="text-xs text-foreground/70 bg-red-500/5 rounded p-3 font-mono border border-red-500/10">{item.youtube_thumbnail_prompt}</p>
                              </div>
                            )}

                            {/* YouTube SEO Engine */}
                            {item.youtube_title && (
                              <div className="bg-charcoal-deep/40 rounded-lg p-4 border border-red-500/20 space-y-3">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs uppercase tracking-wider text-red-400 font-semibold flex items-center gap-1.5">
                                    <Youtube className="w-4 h-4" /> YouTube Shorts SEO
                                  </p>
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      const pkg = [
                                        `TITLE: ${item.youtube_title}`,
                                        `\nDESCRIPTION:\n${item.youtube_description}`,
                                        `\nTAGS: ${(item.youtube_tags || []).join(", ")}`,
                                        `\nPINNED COMMENT:\n${item.youtube_pinned_comment}`,
                                      ].join("\n");
                                      copyToClipboard(pkg, "YouTube Package");
                                    }}
                                    className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 text-xs h-7"
                                  >
                                    <Copy className="w-3 h-3 mr-1" /> Copy YouTube Package
                                  </Button>
                                </div>

                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Search-Focused Title</p>
                                  <div className="flex items-center justify-between bg-charcoal-deep rounded p-2 border border-glass-border">
                                    <p className="text-sm text-foreground font-semibold">{item.youtube_title}</p>
                                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.youtube_title, "Title")} className="h-5 w-5 p-0 text-muted-foreground">
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">{item.youtube_title.length}/60 chars</p>
                                </div>

                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">SEO Description</p>
                                  <div className="flex items-start justify-between bg-charcoal-deep rounded p-2 border border-glass-border">
                                    <p className="text-xs text-foreground/80 whitespace-pre-wrap">{item.youtube_description}</p>
                                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.youtube_description, "Description")} className="h-5 w-5 p-0 text-muted-foreground shrink-0">
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>

                                {item.youtube_tags && item.youtube_tags.length > 0 && (
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Hash className="w-3 h-3" /> Tags</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {item.youtube_tags.map((tag, i) => (
                                        <Badge key={i} className="bg-charcoal-dark border-glass-border text-[10px] text-muted-foreground cursor-pointer hover:text-red-400 hover:border-red-500/30" onClick={() => copyToClipboard(tag, "Tag")}>
                                          {tag}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">📌 Pinned Comment</p>
                                  <div className="flex items-start justify-between bg-charcoal-deep rounded p-2 border border-glass-border">
                                    <p className="text-xs text-foreground/80">{item.youtube_pinned_comment}</p>
                                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.youtube_pinned_comment, "Pinned comment")} className="h-5 w-5 p-0 text-muted-foreground shrink-0">
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Overlay Text */}
                            {item.overlay_text && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-[10px] uppercase tracking-wider text-yellow-400">📊 Viral Overlay Text</p>
                                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.overlay_text, "Overlay text")} className="h-6 text-xs text-yellow-400">
                                    <Copy className="w-3 h-3 mr-1" />Copy
                                  </Button>
                                </div>
                                <p className="text-xs text-foreground/80 bg-yellow-500/5 rounded p-3 font-mono border border-yellow-500/10">{item.overlay_text}</p>
                              </div>
                            )}

                            {/* Viral Hooks */}
                            {item.viral_hooks && item.viral_hooks.length > 0 && (
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-red-400 mb-1">🔥 Viral Hooks</p>
                                <div className="space-y-1">
                                  {item.viral_hooks.map((hook, i) => (
                                    <div key={i} className="flex items-center justify-between bg-red-500/5 rounded p-2 border border-red-500/10">
                                      <p className="text-xs text-foreground/80 font-mono">{hook}</p>
                                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(hook, `Hook ${i + 1}`)} className="h-5 w-5 p-0 text-red-400">
                                        <Copy className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Caption */}
                            {item.caption && (
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-[10px] uppercase tracking-wider text-green-400">📝 Caption</p>
                                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(item.caption, "Caption")} className="h-6 text-xs text-green-400">
                                    <Copy className="w-3 h-3 mr-1" />Copy
                                  </Button>
                                </div>
                                <p className="text-xs text-foreground/70 bg-green-500/5 rounded p-3 border border-green-500/10">{item.caption}</p>
                              </div>
                            )}

                            {/* Bottom Actions */}
                            <div className="flex items-center gap-3 pt-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <Switch checked={item.is_viral} onCheckedChange={(val) => handleViralToggle(item, val)} />
                                <span className="text-xs text-muted-foreground">Mark as Viral</span>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleMarkAsPosted(item)}
                                disabled={item.status === "posted"}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30 text-xs"
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark as Posted
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-glass-border text-xs"
                                onClick={() => copyToClipboard("I've decoded this! Check my latest video for the breakdown. 🔍🧠 #GreyRockAI #SilenceIsPower", "Reply")}
                              >
                                <Copy className="w-3 h-3 mr-1" /> Copy Reply
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Add Request Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-charcoal-dark border-glass-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Add Social Media Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Platform</label>
              <Select value={newPlatform} onValueChange={setNewPlatform}>
                <SelectTrigger className="bg-charcoal-deep border-glass-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tiktok">🎵 TikTok</SelectItem>
                  <SelectItem value="instagram">📸 Instagram</SelectItem>
                  <SelectItem value="youtube">▶️ YouTube</SelectItem>
                  <SelectItem value="twitter">𝕏 Twitter/X</SelectItem>
                  <SelectItem value="facebook">📘 Facebook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Username</label>
              <Input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="@username"
                className="bg-charcoal-deep border-glass-border"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Comment Text</label>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Paste the toxic comment here..."
                rows={4}
                className="bg-charcoal-deep border-glass-border font-mono text-sm"
              />
            </div>
            <Button onClick={handleAddRequest} className="w-full bg-cyan hover:bg-cyan/80 text-charcoal-deep font-semibold">
              <Plus className="w-4 h-4 mr-1" /> Add to Queue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </CommandLayout>
  );
};

export default SocialWarRoom;
